import { tenantConfig } from '../config/tenant'

interface Props {
  /** Height in Tailwind units; e.g. 10 → h-10 (40px). Default: 16 (64px). */
  size?: 10 | 12 | 14 | 16 | 20 | 24 | 28 | 32
  /** Show the tenant name text next to the logo image */
  withText?: boolean
  /** Text color when `withText` is true; defaults to `text-ink` (works on light chrome). */
  textClassName?: string
}

const SIZE_CLASS: Record<10 | 12 | 14 | 16 | 20 | 24 | 28 | 32, string> = {
  10: 'h-10', 12: 'h-12', 14: 'h-14', 16: 'h-16', 20: 'h-20', 24: 'h-24', 28: 'h-28', 32: 'h-32',
}

/**
 * Renders the tenant logo. Uses tenantConfig.logoImageUrl when set (image mode);
 * otherwise falls back to a coloured square with tenantConfig.logoMonogram.
 * All chrome components (Nav, Footer, LoginPage, DataEntryPage) call this so the
 * tenant switch is one edit, not five.
 */
export default function Logo({ size = 16, withText = false, textClassName = 'text-ink' }: Props) {
  const heightCls = SIZE_CLASS[size]
  const hasImage = !!tenantConfig.logoImageUrl

  return (
    <span className="flex items-center gap-2.5">
      {hasImage ? (
        <img
          src={tenantConfig.logoImageUrl}
          alt={`${tenantConfig.name} logo`}
          className={`${heightCls} w-auto rounded-lg object-contain`}
        />
      ) : (
        <span
          className={`${heightCls} aspect-square rounded-lg flex items-center justify-center`}
          style={{ background: tenantConfig.primaryColor }}
          aria-hidden="true"
        >
          <span className="font-heading font-bold text-base" style={{ color: tenantConfig.accentColor }}>
            {tenantConfig.logoMonogram}
          </span>
        </span>
      )}
      {withText && (
        <span className={`font-heading font-semibold text-base hidden sm:block ${textClassName}`}>
          {tenantConfig.name}
        </span>
      )}
    </span>
  )
}
